/** Browsers we target for admin/partner web push (FCM). */
export const SUPPORTED_PUSH_BROWSER_LABEL =
  'Google Chrome, Microsoft Edge, Mozilla Firefox, Safari (macOS 16.4+), and Safari on iPhone/iPad (Home Screen app)';

const detectIos = (ua, platform, maxTouchPoints) =>
  /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);

/**
 * Capability-based profile — do not gate push on Chrome-only UA checks.
 * FCM web push uses the standard Push API + service worker (Chromium, Firefox, Safari 16.4+).
 */
export const getBrowserPushProfile = () => {
  if (typeof navigator === 'undefined') {
    return {
      name: 'unknown',
      isChrome: false,
      isEdge: false,
      isFirefox: false,
      isOpera: false,
      isBrave: false,
      isSafari: false,
      isChromium: false,
      isIos: false,
      isMacSafari: false,
      isStandalonePwa: false,
      isSupportedTarget: false,
    };
  }

  const ua = navigator.userAgent;
  const platform = navigator.platform;
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;

  const isIos = detectIos(ua, platform, maxTouchPoints);
  const isEdge = /Edg\//.test(ua);
  const isOpera = /OPR\//.test(ua) || /Opera/i.test(ua);
  const isBrave = /Brave/i.test(ua);
  const isFirefox = /Firefox\//.test(ua) && !/Seamonkey/i.test(ua);
  const isChrome =
    /Chrome\//.test(ua) && !isEdge && !isOpera && !isFirefox && !/Chromium/i.test(ua);
  const isSafari =
    /Safari/i.test(ua) &&
    !isChrome &&
    !isEdge &&
    !isOpera &&
    !isFirefox &&
    !/Chromium|CriOS|FxiOS/i.test(ua);
  const isStandalonePwa =
    window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    window.navigator.standalone === true;

  const isMacSafari = isSafari && !isIos;
  const isChromium = isChrome || isEdge || isOpera || isBrave;
  const isSupportedTarget = isChromium || isFirefox || isMacSafari || (isIos && isStandalonePwa);

  let name = 'unknown';
  if (isEdge) name = 'edge';
  else if (isOpera) name = 'opera';
  else if (isBrave) name = 'brave';
  else if (isFirefox) name = 'firefox';
  else if (isChrome) name = 'chrome';
  else if (isMacSafari) name = 'safari-mac';
  else if (isSafari && isIos) name = 'safari-ios';

  return {
    name,
    isChrome,
    isEdge,
    isFirefox,
    isOpera,
    isBrave,
    isSafari,
    isChromium,
    isIos,
    isMacSafari,
    isStandalonePwa,
    isSupportedTarget,
  };
};

export const hasWebPushCapabilities = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined'
  );
};

export const isSecureWebPushContext = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.isSecureContext === true;
};

/**
 * Pre-flight check before calling Firebase getToken().
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export const getWebPushEnvironmentCheck = () => {
  const profile = getBrowserPushProfile();

  if (profile.isIos && !profile.isStandalonePwa) {
    return { ok: false, reason: 'safari-ios-pwa-required' };
  }

  if (!profile.isSupportedTarget) {
    return { ok: false, reason: 'unsupported-browser' };
  }

  if (!isSecureWebPushContext()) {
    return { ok: false, reason: 'insecure-context' };
  }

  if (!hasWebPushCapabilities()) {
    return { ok: false, reason: 'unsupported-browser' };
  }

  return { ok: true };
};

/** Safari requires PushManager.subscribe during the same user gesture as the click. */
export const requiresPushUserGesture = () => {
  const profile = getBrowserPushProfile();
  return profile.isMacSafari || (profile.isIos && profile.isStandalonePwa);
};
